import { useCallback } from "react";
import { useDispatch } from "react-redux";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
// @ts-ignore
import pdfMake from "pdfmake/build/pdfmake";
import { fetchQuotationById, changeQuotationStatusById } from "slices/Quotation/thunk";
import { generateQuotationPDF } from "helpers/PDF/generateQuotationPDF";

export const useHandleDownloadPIInvoice1 = () => {
  const dispatch = useDispatch<any>();

  const handlePIInvoice1 = useCallback(
    async (id: any, onSuccess?: () => void) => {
      // Fetch quotation data once for both status check and PDF generation
      let quotationData;
      try {
        quotationData = await dispatch(fetchQuotationById(id)).unwrap();
      } catch (error) {
        console.error("Failed to fetch quotation data:", error);
        toast.error("Failed to load quotation data. Please try again.");
        return;
      }

      const currentStatus = quotationData.current_status;

      // Only show confirmation dialog if status is not "PI Generated"
      if (currentStatus !== "PI Generated") {
        try {
          const confirmResult = await Swal.fire({
            title: "Generate Pro Forma Invoice?",
            text: "After generating PI Invoice, the quotation status will be updated to PI Generated. Are you sure you want to proceed?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, Generate PI",
            cancelButtonText: "Cancel",
            customClass: { popup: "swal2-sm-popup" },
          });

          if (!confirmResult.isConfirmed) {
            return;
          }
        } catch (error) {
          console.error("Confirmation dialog error:", error);
          return;
        }
      }

      let isCancelled = false;

      //*// Frontend PI Invoice 1 Download
      Swal.fire({
        title: "Generating...",
        html: `
        <p>Please wait while your Pro Forma Invoice is being generated.</p>
        <button id="cancelPI" class="swal2-cancel swal2-styled" style="display:inline-block">
          Cancel
        </button>
      `,
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();

          const cancelBtn = Swal.getPopup()?.querySelector("#cancelPI");
          if (cancelBtn) {
            cancelBtn.addEventListener("click", () => {
              isCancelled = true;
              Swal.close();
              Swal.fire({
                icon: "info",
                title: "Cancelled",
                timer: 1000,
                showConfirmButton: false,
                customClass: { popup: "swal2-sm-popup" },
              });
            });
          }
        },
        customClass: { popup: "swal2-sm-popup" },
      });

      try {
        // Check if cancelled after data fetch
        if (isCancelled) return;

        const quotationPDF = await generateQuotationPDF(quotationData, 'PRO FORMA INVOICE');

        // Check if cancelled after PDF generation
        if (isCancelled) return;

        // Set filename in the PDF document definition
        const filename = quotationData?.quotation_auto_id ? `PI-${quotationData.quotation_auto_id}.pdf` : 'PI-Invoice.pdf';
        const pdfDocWithInfo = quotationPDF as any;
        if (pdfDocWithInfo.info) {
          pdfDocWithInfo.info.title = filename.replace('.pdf', '');
        } else {
          pdfDocWithInfo.info = { title: filename.replace('.pdf', '') };
        }

        // Download PDF directly with correct filename
        if (!isCancelled) {
          pdfMake.createPdf(quotationPDF).download(filename);

          // Update quotation status to "PI Generated" after successful PDF generation
          // Only update if current status is not already "PI Generated"
          if (quotationData.current_status !== "PI Generated") {
            try {
              await dispatch(changeQuotationStatusById({ id: id, payload: "PI Generated" })).unwrap();
              console.log("Quotation status updated to PI Generated");
              // Call success callback to refresh data
              if (onSuccess) {
                onSuccess();
              }
            } catch (statusError: any) {
              console.error("Failed to update quotation status:", statusError);
              // Don't show error to user as PDF was successfully generated
            }
          }

          Swal.close();
        }

        Swal.close();
      } catch (err: any) {
        console.log(err, "err--------11");
        if (!isCancelled) {
          Swal.close();
          Swal.fire({
            icon: "error",
            title: "Failed",
            text: "Failed to generate Pro Forma Invoice PDF.",
            customClass: { popup: "swal2-sm-popup" },
          });
        }
      }
      return;

    },
    [dispatch]
  );

  return handlePIInvoice1;
};

export const useHandleDownloadPDF = () => {
  const dispatch = useDispatch<any>();

  const handleDownloadPDF = useCallback(
    async (id: any) => {
      let isCancelled = false;

      //*// Frontend PDF Download
      Swal.fire({
        title: "Generating PDF...",
        html: `
        <p>Please wait while your PDF is being generated.</p>
        <button id="cancelPDF" class="swal2-cancel swal2-styled" style="display:inline-block">
          Cancel
        </button>
      `,
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();

          const cancelBtn = Swal.getPopup()?.querySelector("#cancelPDF");
          if (cancelBtn) {
            cancelBtn.addEventListener("click", () => {
              isCancelled = true;
              Swal.close();
              Swal.fire({
                icon: "info",
                title: "Cancelled",
                timer: 1000,
                showConfirmButton: false,
                customClass: { popup: "swal2-sm-popup" },
              });
            });
          }
        },
        customClass: { popup: "swal2-sm-popup" },
      });

      try {
        const quotationData = await dispatch(fetchQuotationById(id)).unwrap();

        // Check if cancelled after data fetch
        if (isCancelled) return;

        const quotationPDF = await generateQuotationPDF(quotationData, 'QUOTATION');

        // Check if cancelled after PDF generation
        if (isCancelled) return;

        // Set filename in the PDF document definition
        const filename = quotationData?.quotation_auto_id ? `Quotation-${quotationData.quotation_auto_id}.pdf` : 'Quotation.pdf';
        const pdfDocWithInfo = quotationPDF as any;
        if (pdfDocWithInfo.info) {
          pdfDocWithInfo.info.title = filename.replace('.pdf', '');
        } else {
          pdfDocWithInfo.info = { title: filename.replace('.pdf', '') };
        }

        // download PDF directly
        if (!isCancelled) {
          pdfMake.createPdf(quotationPDF).download(filename);
          Swal.close();
        }

        Swal.close();
      } catch (err: any) {
        console.log(err, "err--------11");
        if (!isCancelled) {
          Swal.close();
          Swal.fire({
            icon: "error",
            title: "Failed",
            text: "Failed to generate quotation PDF.",
            customClass: { popup: "swal2-sm-popup" },
          });
        }
      }
      return;

      //*// Backend PDF Download
      // const controller = new AbortController();

      // Swal.fire({
      //   title: "Downloading...",
      //   html: `
      //   <p>Please wait while your PDF is being generated.</p>
      //   <button id="cancelDownload" class="swal2-cancel swal2-styled" style="display:inline-block">
      //     Cancel
      //   </button>
      // `,
      //   allowOutsideClick: false,
      //   showConfirmButton: false,
      //   didOpen: () => {
      //     Swal.showLoading();

      //     const cancelBtn = Swal.getPopup()?.querySelector("#cancelDownload");
      //     if (cancelBtn) {
      //       cancelBtn.addEventListener("click", () => {
      //         controller.abort(); // 👈 abort Axios request
      //         Swal.close();
      //         Swal.fire({
      //           icon: "info",
      //           title: "Cancelled",
      //           // text: "Download was Cancelled",
      //           timer: 1000,
      //           showConfirmButton: false,
      //           customClass: { popup: "swal2-sm-popup" },
      //         });
      //       });
      //     }
      //   },
      //   customClass: { popup: "swal2-sm-popup" },
      // });

      // try {
      //   await dispatch(
      //     downloadQuotationPDF({ id, signal: controller.signal })
      //   ).unwrap();

      //   Swal.close();
      //   Swal.fire({
      //     icon: "success",
      //     title: "PDF Downloaded Sucessfully",
      //     // text: "Your quotation PDF is downloading.",
      //     timer: 2000,
      //     showConfirmButton: false,
      //     customClass: { popup: "swal2-sm-popup" },
      //   });
      // } catch (err: any) {
      //   if (err === "Download cancelled by user.") return; // already handled
      //   Swal.close();
      //   Swal.fire({
      //     icon: "error",
      //     title: "Failed",
      //     text: "Failed to download quotation PDF.",
      //     customClass: { popup: "swal2-sm-popup" },
      //   });
      // }
    },
    [dispatch]
  );

  return handleDownloadPDF;
};